import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import { CurrencyProvider } from 'contexts/currencyContext'
import { last } from 'lib/array/arrayUtils'
import flightBundleGlossaryMessages from 'i18n/flightBundleGlossaryMessages'
import { getTotalStopCount } from 'lib/flights/flightUtils'

interface Props {
  journey: App.JourneyV2
  onSelect?: (data: { journey: App.JourneyV2 }) => void
  onViewDetails?: () => void
  isAlreadyAdded?: boolean
  isSelectDisabled?: boolean
  selectLabel?: string
  showSelectButton?: boolean
  showPrice?: boolean
  alignColumns?: boolean
  responsiveCompact?: boolean
  compactVariant?: 'default' | 'recommendation'
}

const COMPACT_CARD_BREAKPOINT = 490

const ResponsiveCardContainer = styled.div`
  container-name: chat-flight-card;
  container-type: inline-size;
  width: 100%;
`

const CardContainer = styled(Sheet)`
  padding: ${rem(16)} ${rem(12)};
`

const RecommendationCardContainer = styled(CardContainer)`
  padding: ${rem(12)};
`

const ResponsiveWideContent = styled.div`
  @container chat-flight-card (max-width: ${rem(COMPACT_CARD_BREAKPOINT)}) {
    display: none;
  }
`

const ResponsiveCompactContent = styled.div`
  display: none;

  @container chat-flight-card (max-width: ${rem(COMPACT_CARD_BREAKPOINT)}) {
    display: block;
  }
`

const AlignedCardGrid = styled.div`
  align-items: center;
  column-gap: clamp(${rem(8)}, 2vw, ${rem(24)});
  row-gap: ${rem(4)};
  display: grid;
  grid-template-columns:
    minmax(0, 1.8fr)
    minmax(0, 0.9fr)
    minmax(0, 0.9fr)
    minmax(0, 1fr)
    max-content;
  width: 100%;

  > * {
    min-width: 0;
  }
`

const PriceCell = styled.div`
  justify-self: start;
  min-width: 0;
`

const SelectCell = styled.div`
  justify-self: end;
  min-width: 0;
`

const CompactCardGrid = styled.div`
  align-items: center;
  column-gap: ${rem(10)};
  display: grid;
  grid-template-areas:
    "logo primary price"
    "logo meta action"
    ". details details";
  grid-template-columns: ${rem(32)} minmax(0, 1fr) max-content;
  row-gap: ${rem(6)};
  width: 100%;

  > * {
    min-width: 0;
  }
`

const CompactLogoCell = styled.div`
  align-self: center;
  grid-area: logo;
`

const CompactPrimaryCell = styled.div`
  grid-area: primary;
`

const CompactMetaCell = styled.div`
  grid-area: meta;
`

const CompactPriceCell = styled.div`
  grid-area: price;
  justify-self: end;
`

const CompactSelectCell = styled.div`
  grid-area: action;
  justify-self: end;
`

const CompactDetailsLink = styled(TextLink)`
  grid-area: details;
  justify-self: end;
`

const RecommendationCompactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  width: 100%;
`

const RecommendationCompactTopRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)} ${rem(16)};
  justify-content: flex-start;
  width: 100%;
`

const RecommendationCompactPrimary = styled.div`
  align-items: center;
  display: flex;
  gap: ${rem(4)};
  min-width: 0;
`

const RecommendationCompactMeta = styled.div`
  display: flex;
  gap: ${rem(12)};
  min-width: 0;
  white-space: nowrap;
`

const RecommendationCompactColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: 0;
`

const RecommendationCompactFooter = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: ${rem(12)};
  width: 100%;
`

const FlightDetailsGrid = styled.div`
  align-items: center;
  column-gap: ${rem(24)};
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) ${rem(56)} ${rem(72)};
  min-width: 0;
`

const PrimaryFlightDetails = styled(Group)`
  min-width: 0;
`

const FlightTimeRange = styled(Heading)`
  white-space: nowrap;
`

const StackedPriceRowPrice = styled(PriceRowPrice)`
  align-items: flex-start;
  flex-direction: column;
  row-gap: 0;
`

const StackedPriceRowLuxPlusPrice = styled(PriceRowLuxPlusPrice)`
  align-items: flex-start;
  flex-direction: column;
  row-gap: 0;
`

const AirlineLogo = styled.img`
  height: ${rem(32)};
  width: ${rem(32)};
  object-fit: contain;
  flex-shrink: 0;
`

function getFirstStopAirport(flights: Array<App.FlightV2>): string {
  return flights.length > 1 ?
    flights[0].arrivalAirport :
    flights[0]?.hiddenStops?.[0]?.airportCode ?? ''
}

function getStopsLabel(numberOfStops: number, flights: Array<App.FlightV2>, intl: IntlShape): string {
  if (numberOfStops === 0) {
    return intl.formatMessage({
      id: 'Cvc6NejS9VBmrNzgU3V9',
      defaultMessage: 'Direct',
      description: 'Label indicating a non-stop direct flight in the trip planner drawer flight card',
    })
  }
  if (numberOfStops === 1) {
    const layoverAirport = getFirstStopAirport(flights)
    return intl.formatMessage(
      {
        id: 'jW7AvVkBqCku8uiTVaDq',
        defaultMessage: '1 stop {airport}',
        description: 'Label for a single layover stop in the trip planner drawer flight card',
      },
      { airport: layoverAirport },
    )
  }
  return intl.formatMessage(
    {
      id: 'TMkiPFK3ZUXBJuoTfJvb',
      defaultMessage: '{count} stops',
      description: 'Label showing the number of layover stops in the trip planner drawer flight card',
    },
    { count: numberOfStops },
  )
}

function getLayoverDetail(numberOfStops: number, flights: Array<App.FlightV2>): string {
  if (numberOfStops === 0) return ''
  const layoverAirport = getFirstStopAirport(flights)
  if (!layoverAirport) return ''
  if (flights.length < 2) return layoverAirport
  const { hours, minutes } = flights[0].stopoverTime
  if (hours || minutes) {
    return `${hours}h ${minutes}m ${layoverAirport}`
  }
  return layoverAirport
}

const DetailsLink = styled(TextLink)`
  grid-column: 5;
  justify-self: center;
`

function DrawerFlightCard({
  journey,
  onSelect,
  onViewDetails,
  isAlreadyAdded = false,
  isSelectDisabled = false,
  selectLabel: selectLabelOverride,
  showSelectButton = true,
  showPrice = true,
  alignColumns = false,
  responsiveCompact = false,
  compactVariant = 'default',
}: Props) {
  const intl = useIntl()

  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = last(flights)
  const duration = journey.flightGroup.totalFlightDuration
  const numberOfStops = getTotalStopCount(flights)

  const logoSrc = journey.carrierBrandMark ?? journey.carrierLogo
  const routeLabel = `${firstFlight.departureAirport}-${lastFlight.arrivalAirport}`

  const durationLabel = `${duration.hours}h ${duration.minutes}m`

  const stopsLabel = useMemo(
    () => getStopsLabel(numberOfStops, flights, intl),
    [numberOfStops, flights, intl],
  )

  const layoverDetail = useMemo(
    () => getLayoverDetail(numberOfStops, flights),
    [numberOfStops, flights],
  )

  const defaultSelectLabel = intl.formatMessage({
    id: 'FOREyFIFyoG8zaEUykey',
    defaultMessage: 'Add to trip',
    description: 'Button label to add a flight to the trip planner itinerary from the drawer search results',
  })

  const addedLabel = intl.formatMessage({
    id: 'yjcRGlZFznW-to4yKCRx',
    defaultMessage: 'Already in trip',
    description: 'Disabled button label shown when a flight is already in the trip planner itinerary',
  })
  const flightDetailsLabel = intl.formatMessage(flightBundleGlossaryMessages.flightDetails)

  const handleClick = useCallback(() => {
    if (!onSelect) return
    onSelect({ journey })
  }, [onSelect, journey])

  const airlineLogo = logoSrc ? <AirlineLogo src={logoSrc} alt={journey.carrierName} /> : null

  const primaryFlightText = (
    <Group direction="vertical" gap={4}>
      <FlightTimeRange variant="heading6" colour="light-foreground-alt">
        {intl.formatMessage(
          {
            id: 'zOG3k0v44GaR0Jn64tgV',
            defaultMessage: '{departureTime} - {arrivalTime}',
            description: 'Flight departure and arrival time range in the trip planner flight drawer card',
          },
          { departureTime: firstFlight.departingTime, arrivalTime: lastFlight.arrivalTime },
        )}
      </FlightTimeRange>
      <Caption variant="medium" colour="light-foreground-alt">
        {journey.carrierName}
      </Caption>
    </Group>
  )

  const primaryFlightDetails = (
    <PrimaryFlightDetails direction="horizontal" gap={8} verticalAlign="center">
      {airlineLogo}
      {primaryFlightText}
    </PrimaryFlightDetails>
  )

  const durationDetails = (
    <Group direction="vertical" gap={2}>
      <BodyText variant="medium" colour="light-foreground-alt">{durationLabel}</BodyText>
      <Caption variant="medium" colour="light-foreground-alt">{routeLabel}</Caption>
    </Group>
  )

  const stopsDetails = (
    <Group direction="vertical" gap={2}>
      <BodyText variant="medium" colour="light-foreground-alt">{stopsLabel}</BodyText>
      {layoverDetail && (
        <Caption variant="medium" colour="light-foreground-alt">{layoverDetail}</Caption>
      )}
    </Group>
  )

  const memberPrice = journey.price.adult.totalFare ?? 0
  const regularPrice = journey.regularPrice?.adult?.totalFare
  const showLuxPlusPrice = journey.bookingInfo.isLuxPlusFare === true && Number.isFinite(regularPrice)

  const priceDetails = showPrice ? (
    <CurrencyProvider currency={journey.currencyCode}>
      {showLuxPlusPrice ?
          <StackedPriceRowLuxPlusPrice size="S" memberPrice={memberPrice} nonMemberPrice={regularPrice} saleUnit="adult" /> :
          <StackedPriceRowPrice size="S" price={memberPrice} saleUnit="adult" />}
    </CurrencyProvider>
  ) : null

  const inlinePriceDetails = showPrice ? (
    <CurrencyProvider currency={journey.currencyCode}>
      {showLuxPlusPrice ?
          <PriceRowLuxPlusPrice size="S" memberPrice={memberPrice} nonMemberPrice={regularPrice} saleUnit="adult" /> :
          <PriceRowPrice size="S" price={memberPrice} saleUnit="adult" />}
    </CurrencyProvider>
  ) : null

  const selectButton = showSelectButton
    ? (
        <TextButton
          kind={isAlreadyAdded ? 'tertiary' : 'secondary'}
          size="small"
          shape={compactVariant === 'recommendation' ? 'round' : 'square'}
          startIcon={isAlreadyAdded ? <LineCheckIcon /> : <LinePlusIcon />}
          onClick={handleClick}
          disabled={isAlreadyAdded || isSelectDisabled}
        >
          {isAlreadyAdded ? addedLabel : (selectLabelOverride ?? defaultSelectLabel)}
        </TextButton>
      )
    : null

  const leftContent = alignColumns
    ? (
        <FlightDetailsGrid>
          {primaryFlightDetails}
          {durationDetails}
          {stopsDetails}
        </FlightDetailsGrid>
      )
    : (
        <Group direction="horizontal" gap={24} verticalAlign="center">
          {primaryFlightDetails}
          {durationDetails}
          {stopsDetails}
        </Group>
      )

  const rightContent = (
    <Group direction="horizontal" gap={16} verticalAlign="center">
      {priceDetails}
      {selectButton}
    </Group>
  )

  const detailsLink = onViewDetails ? (
    <DetailsLink size="caption" weight="regular" onClick={onViewDetails}>
      {flightDetailsLabel}
    </DetailsLink>
  ) : null

  const compactDetailsLink = onViewDetails ? (
    <CompactDetailsLink size="caption" weight="regular" onClick={onViewDetails}>
      {flightDetailsLabel}
    </CompactDetailsLink>
  ) : null

  const compactCardContent = (
    <CompactCardGrid>
      <CompactLogoCell>{airlineLogo}</CompactLogoCell>
      <CompactPrimaryCell>{primaryFlightText}</CompactPrimaryCell>
      <CompactMetaCell>
        <Group direction="horizontal" gap={8} wrap="wrap" verticalAlign="center">
          <BodyText variant="small" colour="light-foreground-alt">{durationLabel}</BodyText>
          <BodyText variant="small" colour="light-foreground-alt">{stopsLabel}</BodyText>
          <Caption variant="medium" colour="light-foreground-alt">{routeLabel}</Caption>
        </Group>
      </CompactMetaCell>
      <CompactPriceCell>{priceDetails}</CompactPriceCell>
      {selectButton && (
        <CompactSelectCell>
          <Group direction="vertical" gap={8} horizontalAlign="center">
            {selectButton}
            {compactDetailsLink}
          </Group>
        </CompactSelectCell>
      )}
      {!selectButton && compactDetailsLink}
    </CompactCardGrid>
  )

  const recommendationCompactCardContent = (
    <RecommendationCompactGrid>
      <RecommendationCompactTopRow>
        <RecommendationCompactPrimary>
          {airlineLogo}
          {primaryFlightText}
        </RecommendationCompactPrimary>
        <RecommendationCompactMeta>
          <RecommendationCompactColumn>
            <BodyText variant="medium" colour="light-foreground-alt">{durationLabel}</BodyText>
            <Caption variant="medium" colour="light-foreground-alt">{routeLabel}</Caption>
          </RecommendationCompactColumn>
          <RecommendationCompactColumn>
            <BodyText variant="medium" colour="light-foreground-alt">{stopsLabel}</BodyText>
            {layoverDetail && (
              <Caption variant="medium" colour="light-foreground-alt">{layoverDetail}</Caption>
            )}
          </RecommendationCompactColumn>
        </RecommendationCompactMeta>
      </RecommendationCompactTopRow>
      <RecommendationCompactFooter>
        {inlinePriceDetails}
        {selectButton}
      </RecommendationCompactFooter>
    </RecommendationCompactGrid>
  )

  const defaultCardContent = alignColumns
    ? (
        <AlignedCardGrid>
          {primaryFlightDetails}
          {durationDetails}
          {stopsDetails}
          <PriceCell>{priceDetails}</PriceCell>
          {selectButton && (
            <SelectCell>
              <Group direction="vertical" gap={12} horizontalAlign="center">
                {selectButton}
                {detailsLink}
              </Group>
            </SelectCell>
          )}
          {!selectButton && detailsLink}
        </AlignedCardGrid>
      )
    : (
        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" fullWidth>
            {leftContent}
            {rightContent}
          </Group>
          {detailsLink}
        </Group>
      )

  const ResponsiveCardShell = compactVariant === 'recommendation' ? RecommendationCardContainer : CardContainer

  const cardContent = responsiveCompact
    ? (
        <ResponsiveCardContainer>
          <ResponsiveCardShell border="light-foreground" borderRadius="L">
            <ResponsiveWideContent>{defaultCardContent}</ResponsiveWideContent>
            <ResponsiveCompactContent>
              {compactVariant === 'recommendation' ? recommendationCompactCardContent : compactCardContent}
            </ResponsiveCompactContent>
          </ResponsiveCardShell>
        </ResponsiveCardContainer>
      )
    : (
        <CardContainer border="light-foreground" borderRadius="L">
          {defaultCardContent}
        </CardContainer>
      )

  return (
    cardContent
  )
}

export default memo(DrawerFlightCard)
